import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import localforage from 'localforage';
import { Link } from 'react-router-dom';
import { useBaseUrl } from '../../utils/utils';
import { showToast } from '../../utils/swal';

const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';

type ServiceOption = {
  service_id: number;
  service_title: string;
  service_name?: string;
  service_subtitle?: string;
};

type CountyOption = {
  county: string;
  county_code: number;
};

type TownOption = {
  town: string;
  town_id: number;
};

type PartnerSummary = {
  clinic_id: number;
  clinic_name: string;
  clinic_capacity?: number;
  clinic_description?: string;
  clinic_website?: string;
  clinic_logo?: string | null;
  services?: Array<{ title?: string; name?: string; minimum_rate?: string; hourly_rate?: string }>;
  category: { cat: string; desc?: string };
  location: { county: string; county_code: number; town: string; town_id: number };
};

type PartnerDetail = {
  partner: string;
  partner_id: number;
  description?: string;
  email?: string;
  website?: string;
  phone?: string;
  capacity?: number;
  logo?: string;
  category: { name: string; descr?: string };
  location: { county: string; county_code: number; town: string; town_id: number };
  services: Array<{
    id: number;
    name: string;
    title: string;
    subtitle?: string;
    minimum_rate?: string;
    hourly_rate?: string;
    is_billable?: boolean;
  }>;
  collaborations: Array<{ id: number; name: string; desc?: string; start?: string; end?: string; patient?: string }>;
};

const FacilityPartners: React.FC = () => {
  const baseUrl = useBaseUrl();
  const [clinicName, setClinicName] = useState('Current Facility');
  const [search, setSearch] = useState('');
  const [selectedService, setSelectedService] = useState<number | ''>('');
  const [selectedCounty, setSelectedCounty] = useState<number | ''>('');
  const [selectedTown, setSelectedTown] = useState<number | ''>('');
  const [serviceOptions, setServiceOptions] = useState<ServiceOption[]>([]);
  const [countyOptions, setCountyOptions] = useState<CountyOption[]>([]);
  const [townOptions, setTownOptions] = useState<TownOption[]>([]);
  const [partners, setPartners] = useState<PartnerSummary[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<PartnerDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  useEffect(() => {
    localforage.getItem<string>('clinic_name').then((value) => {
      if (value) setClinicName(value);
    });
  }, []);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [servicesRes, countiesRes] = await Promise.all([
          axios.get(`${baseUrl}facilities/service-list`, { timeout: 10000 }),
          axios.post(`${baseUrl}facilities/get-counties`, new FormData(), { timeout: 10000 }),
        ]);

        if (servicesRes.data?.status === 'success' && Array.isArray(servicesRes.data.data)) {
          setServiceOptions(servicesRes.data.data);
        }

        if (countiesRes.data?.status === 'success' && Array.isArray(countiesRes.data.data)) {
          setCountyOptions(countiesRes.data.data);
        }
      } catch (error) {
        console.error('Failed to load partner filters:', error);
      }
    };

    loadMeta();
  }, [baseUrl]);

  useEffect(() => {
    const loadTowns = async () => {
      if (!selectedCounty) {
        setTownOptions([]);
        setSelectedTown('');
        return;
      }

      try {
        const response = await axios.post(
          `${baseUrl}facilities/get-towns`,
          { county_id: selectedCounty },
          { timeout: 10000 }
        );

        if (response.data?.status === 'success' && Array.isArray(response.data.data)) {
          setTownOptions(response.data.data);
        } else {
          setTownOptions([]);
        }
      } catch (error) {
        console.error('Failed to load towns:', error);
        setTownOptions([]);
      }
    };

    loadTowns();
  }, [baseUrl, selectedCounty]);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(async () => {
      setIsLoading(true);
      try {
        const payload: Record<string, number> = {};
        if (selectedService) payload.service_id = selectedService;
        if (selectedTown) payload.town_id = selectedTown;

        const response = await axios.post(`${baseUrl}facilities/clinics`, payload, { timeout: 12000 });
        if (!active) return;

        if (response.data?.status === 'success' && Array.isArray(response.data.data)) {
          setPartners(response.data.data);
        } else {
          setPartners([]);
        }
      } catch (error) {
        if (active) {
          console.error('Failed to load partners:', error);
          setPartners([]);
        }
      } finally {
        if (active) setIsLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [baseUrl, selectedService, selectedTown]);

  const filteredPartners = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return partners;

    return partners.filter((item) => {
      const haystack = [
        item.clinic_name,
        item.category?.cat,
        item.location?.county,
        item.location?.town,
        item.clinic_description,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [partners, search]);

  const loadPartnerDetails = async (partnerId: number) => {
    setIsDetailLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl}facilities/partner`,
        { partner: partnerId },
        { timeout: 12000 }
      );

      if (response.data?.status === 'success' && response.data.data) {
        setSelectedPartner(response.data.data);
        return;
      }

      showToast({ icon: 'warning', title: 'Unable to load partner', text: response.data?.message || 'Try again shortly.' });
    } catch (error) {
      console.error('Failed to load partner details:', error);
      showToast({ icon: 'error', title: 'Partner unavailable', text: 'We could not load that partner right now.' });
    } finally {
      setIsDetailLoading(false);
    }
  };

  const resetFilters = () => {
    setSearch('');
    setSelectedService('');
    setSelectedCounty('');
    setSelectedTown('');
    setSelectedPartner(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="sc-card" style={{ margin: 0 }}>
        <div className="sc-card-body" style={{ padding: '18px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: '0.76rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: ACCENT }}>Partner Finder</div>
              <div style={{ fontSize: '0.92rem', fontWeight: 700, marginTop: 4 }}>Discover facilities and services you can collaborate with</div>
              <div style={{ fontSize: '0.76rem', color: 'var(--uzima-shell-text-muted)', marginTop: 4 }}>Searching as {clinicName}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Link to="/institution/collaborations/find-requests" className="sc-btn sc-btn-primary sc-btn-sm" style={{ textDecoration: 'none' }}>
                <i className="bx bx-network-chart"></i> Collaboration Center
              </Link>
              <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={resetFilters}>
                <i className="bx bx-reset"></i> Reset
              </button>
            </div>
          </div>

          <div className="sc-stats" style={{ marginBottom: 0 }}>
            <div className="sc-stat">
              <div className="sc-stat-icon teal"><i className="bx bx-building-house"></i></div>
              <div className="sc-stat-value">{filteredPartners.length}</div>
              <div className="sc-stat-label">Visible Partners</div>
            </div>
            <div className="sc-stat">
              <div className="sc-stat-icon blue"><i className="bx bx-briefcase-alt-2"></i></div>
              <div className="sc-stat-value">{serviceOptions.length}</div>
              <div className="sc-stat-label">Service Types</div>
            </div>
            <div className="sc-stat">
              <div className="sc-stat-icon amber"><i className="bx bx-map"></i></div>
              <div className="sc-stat-value">{selectedCounty ? 1 : countyOptions.length}</div>
              <div className="sc-stat-label">County Scope</div>
            </div>
            <div className="sc-stat">
              <div className="sc-stat-icon violet"><i className="bx bx-star"></i></div>
              <div className="sc-stat-value">{selectedPartner?.services?.length || 0}</div>
              <div className="sc-stat-label">Selected Services</div>
            </div>
          </div>
        </div>
      </div>

      <div className="sc-card" style={{ margin: 0 }}>
        <div className="sc-card-body" style={{ padding: '16px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 10 }}>
            <div className="sc-search" style={{ marginBottom: 0 }}>
              <i className="bx bx-search"></i>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by facility, category, county, or town..." />
            </div>
            <select className="form-control" value={selectedService} onChange={(e) => setSelectedService(e.target.value ? Number(e.target.value) : '')}>
              <option value="">All services</option>
              {serviceOptions.map((item) => (
                <option key={item.service_id} value={item.service_id}>{item.service_title}</option>
              ))}
            </select>
            <select className="form-control" value={selectedCounty} onChange={(e) => setSelectedCounty(e.target.value ? Number(e.target.value) : '')}>
              <option value="">All counties</option>
              {countyOptions.map((item) => (
                <option key={item.county_code} value={item.county_code}>{item.county}</option>
              ))}
            </select>
            <select className="form-control" value={selectedTown} disabled={!selectedCounty || townOptions.length === 0} onChange={(e) => setSelectedTown(e.target.value ? Number(e.target.value) : '')}>
              <option value="">All towns</option>
              {townOptions.map((item) => (
                <option key={item.town_id} value={item.town_id}>{item.town}</option>
              ))}
            </select>
            <button className="sc-btn sc-btn-ghost" onClick={() => setSelectedPartner(null)}>
              <i className="bx bx-x"></i> Clear Focus
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedPartner ? '1.2fr 0.8fr' : '1fr', gap: 16, alignItems: 'start' }}>
        <div className="sc-card" style={{ margin: 0 }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--uzima-shell-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-world me-2" style={{ color: ACCENT }}></i>Available Partners</div>
            <span style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>{filteredPartners.length} results</span>
          </div>
          <div className="sc-card-body">
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '1.8rem', color: ACCENT }}></i>
                <div style={{ marginTop: 10, fontSize: '0.78rem', color: 'var(--uzima-shell-text-muted)' }}>Refreshing partner list...</div>
              </div>
            ) : filteredPartners.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '36px 20px' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 14px', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="bx bx-search-alt" style={{ color: ACCENT, fontSize: '1.4rem' }}></i>
                </div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>No partners match this view</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-muted)' }}>Try a different service, county, town, or search phrase.</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                {filteredPartners.map((item) => {
                  const isSelected = selectedPartner?.partner_id === item.clinic_id;
                  return (
                    <button
                      key={item.clinic_id}
                      type="button"
                      onClick={() => loadPartnerDetails(item.clinic_id)}
                      style={{
                        textAlign: 'left',
                        border: isSelected ? `1px solid ${ACCENT}40` : '1px solid var(--uzima-shell-border)',
                        background: isSelected ? ACCENT_SOFT : '#fff',
                        borderRadius: 16,
                        padding: 16,
                        transition: 'all 0.16s ease',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: isSelected ? ACCENT : 'rgba(15,23,42,0.06)', color: isSelected ? '#fff' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className="bx bx-building-house" style={{ fontSize: '1.1rem' }}></i>
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.84rem' }}>{item.clinic_name}</div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--uzima-shell-text-muted)' }}>{item.category?.cat || 'Partner'}</div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gap: 6, fontSize: '0.73rem', color: 'var(--uzima-shell-text-soft)' }}>
                        <div><i className="bx bx-map me-1"></i>{item.location?.town}, {item.location?.county}</div>
                        <div><i className="bx bx-layer me-1"></i>{item.services?.length || 0} listed services</div>
                        <div><i className="bx bx-pulse me-1"></i>Capacity {item.clinic_capacity || 0}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {selectedPartner && (
          <div className="sc-card" style={{ margin: 0 }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--uzima-shell-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-id-card me-2" style={{ color: ACCENT }}></i>Partner Profile</div>
              {isDetailLoading && <i className="bx bx-loader-alt bx-spin" style={{ color: ACCENT }}></i>}
            </div>
            <div className="sc-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={{ fontSize: '0.96rem', fontWeight: 700 }}>{selectedPartner.partner}</div>
                <div style={{ fontSize: '0.74rem', color: 'var(--uzima-shell-text-muted)' }}>{selectedPartner.category?.name || 'Partner'} • {selectedPartner.location?.town}, {selectedPartner.location?.county}</div>
              </div>

              <div style={{ display: 'grid', gap: 8, fontSize: '0.76rem' }}>
                {selectedPartner.email && <div><strong>Email:</strong> {selectedPartner.email}</div>}
                {selectedPartner.phone && <div><strong>Phone:</strong> {selectedPartner.phone}</div>}
                {selectedPartner.website && <div><strong>Website:</strong> {selectedPartner.website}</div>}
                <div><strong>Capacity:</strong> {selectedPartner.capacity || 0}</div>
              </div>

              {selectedPartner.description && (
                <div style={{ padding: '12px 14px', borderRadius: 12, background: 'rgba(15,23,42,0.03)', fontSize: '0.76rem', lineHeight: 1.55, color: 'var(--uzima-shell-text-soft)' }}>
                  {selectedPartner.description}
                </div>
              )}

              <div>
                <div style={{ fontWeight: 700, fontSize: '0.78rem', marginBottom: 10 }}>Available Services</div>
                {selectedPartner.services?.length ? (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {selectedPartner.services.map((service) => (
                      <div key={service.id} style={{ border: '1px solid var(--uzima-shell-border)', borderRadius: 12, padding: '12px 14px' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.76rem' }}>{service.title || service.name}</div>
                        {service.subtitle && <div style={{ fontSize: '0.7rem', color: 'var(--uzima-shell-text-muted)', marginTop: 3 }}>{service.subtitle}</div>}
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 10, fontSize: '0.7rem', color: 'var(--uzima-shell-text-soft)' }}>
                          <span>Base {service.minimum_rate || '—'}</span>
                          <span>Hourly {service.hourly_rate || '—'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.74rem', color: 'var(--uzima-shell-text-muted)' }}>No shared services are published for this partner yet.</div>
                )}
              </div>

              <div>
                <div style={{ fontWeight: 700, fontSize: '0.78rem', marginBottom: 10 }}>Existing Collaborations</div>
                {selectedPartner.collaborations?.length ? (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {selectedPartner.collaborations.slice(0, 4).map((item) => (
                      <div key={item.id} style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(59,130,246,0.06)', fontSize: '0.72rem' }}>
                        <div style={{ fontWeight: 700 }}>{item.name}</div>
                        <div style={{ color: 'var(--uzima-shell-text-muted)', marginTop: 3 }}>{item.desc || 'Active collaboration'}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.74rem', color: 'var(--uzima-shell-text-muted)' }}>No collaborations are currently visible with this partner.</div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Link to="/institution/collaborations/find-requests" className="sc-btn sc-btn-primary sc-btn-sm" style={{ textDecoration: 'none' }}>
                  <i className="bx bx-send"></i> Create Request
                </Link>
                <Link to="/institution/clinics" className="sc-btn sc-btn-ghost sc-btn-sm" style={{ textDecoration: 'none' }}>
                  <i className="bx bx-buildings"></i> Facility Directory
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FacilityPartners;
